import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Users, Search, Mail, Phone, Calendar, Loader2, UserPlus, Trash2, Copy, CheckCircle, Upload, Download, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Members = () => {
  const { user, token, isOrganizer } = useAuth();
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [batchFilter, setBatchFilter] = useState('all');
  
  // Add Attendee Modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addingAttendee, setAddingAttendee] = useState(false);
  const [newAttendee, setNewAttendee] = useState({
    email: '',
    first_name: '',
    last_name: '',
    batch_year: '',
    phone: ''
  });
  const [createdCredentials, setCreatedCredentials] = useState(null);

  // Bulk Import Modal
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [csvPreview, setCsvPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  const canManage = isOrganizer && isOrganizer();

  useEffect(() => {
    if (canManage) {
      fetchEvents();
    } else {
      if (user?.event_id) {
        setSelectedEventId(user.event_id);
      }
    }
  }, [user]);

  useEffect(() => {
    if (selectedEventId) {
      fetchMembers();
    }
  }, [selectedEventId]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEventId(response.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load events');
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/event/attendees?event_id=${selectedEventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAttendee = async () => {
    if (!newAttendee.email || !newAttendee.first_name || !newAttendee.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAddingAttendee(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/event/attendees/add?event_id=${selectedEventId}`,
        newAttendee,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Attendee added successfully!');
      setCreatedCredentials({
        email: newAttendee.email,
        password: response.data.temp_password,
        name: `${newAttendee.first_name} ${newAttendee.last_name}`
      });
      setNewAttendee({ email: '', first_name: '', last_name: '', batch_year: '', phone: '' });
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add attendee');
    } finally {
      setAddingAttendee(false);
    }
  };

  const handleDeleteAttendee = async (attendeeId, attendeeName) => {
    if (!window.confirm(`Are you sure you want to remove ${attendeeName}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/event/attendees/${attendeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Attendee removed');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to remove attendee');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Bulk Import Functions
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setCsvContent(content);
      parseCSVPreview(content);
    };
    reader.readAsText(file);
  };

  const parseCSVPreview = (content) => {
    const lines = content.split('\n').filter(line => line.trim());
    const preview = lines.slice(0, 6).map(line => {
      const cols = line.split(',').map(col => col.trim());
      return {
        email: cols[0] || '',
        first_name: cols[1] || '',
        last_name: cols[2] || '',
        batch_year: cols[3] || '',
        phone: cols[4] || ''
      };
    });
    setCsvPreview(preview);
  };

  const downloadTemplate = () => {
    const template = 'email,first_name,last_name,batch_year,phone\njuan.delacruz@email.com,Juan,Dela Cruz,1995,09171234567\nmaria.santos@email.com,Maria,Santos,1995,';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attendees_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const handleBulkImport = async () => {
    if (!csvContent) {
      toast.error('Please upload a CSV file first');
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/event/attendees/bulk-import?event_id=${selectedEventId}`,
        { csv_data: csvContent, skip_header: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setImportResults(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} attendees!`);
        fetchMembers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import attendees');
    } finally {
      setImporting(false);
    }
  };

  const resetBulkImport = () => {
    setCsvContent('');
    setCsvPreview([]);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const batchYears = [...new Set(members.filter(m => m.batch_year).map(m => m.batch_year))].sort((a, b) => b - a);

  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      member.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesBatch = batchFilter === 'all' || member.batch_year?.toString() === batchFilter;
    
    return matchesSearch && matchesBatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-maroon" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="members-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <Users className="h-8 w-8" />
              {canManage ? 'Attendees' : 'Batchmates'}
            </h1>
            <p className="text-gray-600 mt-1">
              {members.length} registered {canManage ? 'attendees' : 'batchmates'}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {canManage && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => setBulkImportOpen(true)}
                  className="border-maroon text-maroon hover:bg-maroon hover:text-white"
                  data-testid="bulk-import-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Bulk Import
                </Button>
                <Button 
                  onClick={() => setAddModalOpen(true)}
                  className="bg-maroon hover:bg-maroon-hover text-white"
                  data-testid="add-attendee-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Attendee
                </Button>
              </>
            )}
            
            {canManage && events.length > 1 && (
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select Event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.event_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-cream-paper border-gray-200"
                  data-testid="member-search-input"
                />
              </div>
              <Select value={batchFilter} onValueChange={setBatchFilter}>
                <SelectTrigger className="w-full md:w-40 bg-cream-paper" data-testid="batch-filter">
                  <SelectValue placeholder="Batch Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Batches</SelectItem>
                  {batchYears.map(year => (
                    <SelectItem key={year} value={year.toString()}>Batch {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Members Grid */}
        {filteredMembers.length === 0 ? (
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600">No {canManage ? 'attendees' : 'batchmates'} found</h3>
              <p className="text-gray-400 mt-1">
                {canManage 
                  ? 'Share your event code to invite attendees' 
                  : 'Try adjusting your search or filters'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMembers.map((member) => (
              <Card 
                key={member.id} 
                className="border-0 shadow-sm bg-white hover:shadow-lg transition-shadow duration-300"
                data-testid={`member-card-${member.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-16 w-16 border-2 border-gold">
                      <AvatarImage src={member.profile_image_file_id ? `${API_URL}/api/files/download/${member.profile_image_file_id}` : member.profile_photo} />
                      <AvatarFallback className="bg-maroon text-white text-lg font-medium">
                        {getInitials(member.first_name, member.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {member.first_name} {member.last_name}
                      </h3>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {member.committees && member.committees.length > 0 ? (
                          member.committees.map((committee, idx) => (
                            <Badge 
                              key={idx}
                              className={`text-xs ${
                                committee.role === 'head' 
                                  ? 'bg-gold text-gray-900' 
                                  : 'bg-maroon/10 text-maroon'
                              }`}
                              data-testid={`committee-badge-${member.id}-${idx}`}
                            >
                              {committee.role === 'head' && '★ '}
                              {committee.name.replace(' Committee', '')}
                            </Badge>
                          ))
                        ) : (
                          <Badge className="text-xs bg-gray-100 text-gray-600">
                            Attendee
                          </Badge>
                        )}
                      </div>
                      {member.batch_year && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-2">
                          <Calendar className="h-3 w-3" />
                          Batch {member.batch_year}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="truncate">{member.email}</span>
                    </div>
                    {member.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{member.phone}</span>
                      </div>
                    )}
                    {canManage && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 text-red-500 hover:text-red-700 hover:bg-red-50 w-full"
                        onClick={() => handleDeleteAttendee(member.id, `${member.first_name} ${member.last_name}`)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Attendee Modal */}
        <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-maroon">Add Attendee Manually</DialogTitle>
              <DialogDescription>
                Add an attendee who cannot register themselves. A temporary password will be generated.
              </DialogDescription>
            </DialogHeader>
            
            {createdCredentials ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 mb-3">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Attendee Created Successfully!</span>
                  </div>
                  <p className="text-sm text-green-600 mb-3">
                    Share these login credentials with <strong>{createdCredentials.name}</strong>:
                  </p>
                  
                  <div className="space-y-2 bg-white rounded p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Email:</span>
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">{createdCredentials.email}</code>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdCredentials.email)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Password:</span>
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">{createdCredentials.password}</code>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdCredentials.password)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setCreatedCredentials(null);
                      setAddModalOpen(false);
                    }}
                  >
                    Done
                  </Button>
                  <Button 
                    className="flex-1 bg-maroon hover:bg-maroon-hover"
                    onClick={() => setCreatedCredentials(null)}
                  >
                    Add Another
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      value={newAttendee.first_name}
                      onChange={(e) => setNewAttendee({...newAttendee, first_name: e.target.value})}
                      placeholder="Juan"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      value={newAttendee.last_name}
                      onChange={(e) => setNewAttendee({...newAttendee, last_name: e.target.value})}
                      placeholder="Dela Cruz"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={newAttendee.email}
                    onChange={(e) => setNewAttendee({...newAttendee, email: e.target.value})}
                    placeholder="juan@example.com"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Batch Year</Label>
                    <Input
                      value={newAttendee.batch_year}
                      onChange={(e) => setNewAttendee({...newAttendee, batch_year: e.target.value})}
                      placeholder="1995"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={newAttendee.phone}
                      onChange={(e) => setNewAttendee({...newAttendee, phone: e.target.value})}
                      placeholder="09171234567"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setAddModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-maroon hover:bg-maroon-hover"
                    onClick={handleAddAttendee}
                    disabled={addingAttendee}
                  >
                    {addingAttendee ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add Attendee
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Import Modal */}
        <Dialog open={bulkImportOpen} onOpenChange={(open) => {
          setBulkImportOpen(open);
          if (!open) resetBulkImport();
        }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-maroon flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Bulk Import Attendees
              </DialogTitle>
              <DialogDescription>
                Upload a CSV file to import multiple attendees at once
              </DialogDescription>
            </DialogHeader>
            
            {importResults ? (
              <div className="space-y-4">
                {/* Import Results */}
                <div className={`p-4 rounded-lg ${importResults.imported > 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {importResults.imported > 0 ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-amber-600" />
                    )}
                    <span className="font-medium">
                      {importResults.imported > 0 ? 'Import Complete!' : 'Import Completed with Issues'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-center mb-4">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-2xl font-bold text-gray-900">{importResults.total_rows}</p>
                      <p className="text-xs text-gray-500">Total Rows</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-2xl font-bold text-green-600">{importResults.imported}</p>
                      <p className="text-xs text-gray-500">Imported</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-2xl font-bold text-amber-600">{importResults.skipped}</p>
                      <p className="text-xs text-gray-500">Skipped</p>
                    </div>
                  </div>
                  
                  {importResults.imported > 0 && (
                    <div className="bg-white rounded p-3 mb-3">
                      <p className="text-sm text-gray-600 mb-1">Default password for all imported attendees:</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-gray-100 px-3 py-2 rounded font-mono text-sm">
                          {importResults.temp_password}
                        </code>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(importResults.temp_password)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Errors */}
                {importResults.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-700 mb-2">Skipped Rows:</h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {importResults.errors.map((err, idx) => (
                        <div key={idx} className="text-sm text-red-600 flex items-start gap-2">
                          <span className="font-mono bg-red-100 px-1 rounded">Row {err.row}</span>
                          <span>{err.email && `(${err.email})`}</span>
                          <span>{err.error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      resetBulkImport();
                      setBulkImportOpen(false);
                    }}
                  >
                    Done
                  </Button>
                  <Button 
                    className="flex-1 bg-maroon hover:bg-maroon-hover"
                    onClick={resetBulkImport}
                  >
                    Import More
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Template Download */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-blue-800">Download CSV Template</h4>
                      <p className="text-sm text-blue-600">Start with our template for best results</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={downloadTemplate}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Template
                    </Button>
                  </div>
                </div>
                
                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Upload CSV File</Label>
                  <div 
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                      csvContent ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-maroon'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {csvContent ? (
                      <div>
                        <CheckCircle className="h-10 w-10 mx-auto text-green-500 mb-2" />
                        <p className="text-sm text-green-700 font-medium">File uploaded</p>
                        <p className="text-xs text-green-600 mt-1">{csvPreview.length - 1} attendees found</p>
                      </div>
                    ) : (
                      <div>
                        <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">Click to upload CSV file</p>
                        <p className="text-xs text-gray-400 mt-1">email, first_name, last_name, batch_year, phone</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
                
                {/* CSV Preview */}
                {csvPreview.length > 0 && (
                  <div className="space-y-2">
                    <Label>Preview (first 5 rows)</Label>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Email</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">First Name</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Last Name</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Batch</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreview.slice(1, 6).map((row, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="px-3 py-2 text-gray-900">{row.email}</td>
                              <td className="px-3 py-2 text-gray-600">{row.first_name}</td>
                              <td className="px-3 py-2 text-gray-600">{row.last_name}</td>
                              <td className="px-3 py-2 text-gray-500">{row.batch_year || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {csvPreview.length > 6 && (
                        <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 text-center">
                          + {csvPreview.length - 6} more rows
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      resetBulkImport();
                      setBulkImportOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-maroon hover:bg-maroon-hover"
                    onClick={handleBulkImport}
                    disabled={importing || !csvContent}
                  >
                    {importing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Import Attendees
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
  );
};

export default Members;
